/**
 * The sprite read (spec 021, D-9 to D-16): one sprite's values as a
 * JSON-serialisable projection in the operation units, with field names that
 * match the operation arguments, so a value can be copied into the next call.
 * A pure read: not an operation, not in `SCENE_OPERATIONS`, never throws for
 * a missing sprite. It carries every authored property that changes what
 * renders or plays (D-13) and never derived marks, raw keyframe tracks, the raw layout
 * or transform objects, anchors, zIndex or editor state. The coverage maps at
 * the end say what becomes of every field of the types it reads; the Shared
 * typecheck fails when a field is added without an entry (D-14).
 */
import type {
  Hinge, Keyframe, Orientation, OrientationLayout, Position, ResponsiveLayout, Sprite, SpriteSize, SpriteState, Transform,
} from '../@types';
import { getBasisOrientation, getResponsivePosition, getResponsiveTransform, isDerived } from '../utils/responsiveLayout';
import { resolveSpriteSize } from '../utils/spriteSize';
import { STAGE_SIZES } from '../runtime/evaluateFrame';
import { backToFront, ORIENTATIONS } from './placement';
import type { OperationMedia, SceneDocument } from './types';
import { playbackToArg, type PlaybackArg } from './playbackArg';
import { keyViews, type KeyView } from './keyNames';
import { describeMotion, motionText } from './describeMotion';

/** One orientation's resolved values. */
export interface SpriteOrientationView {
  /** % of the stage, the hinge point, rounded to 0.1 (as in `describeScene`). */
  x: number;
  y: number;
  /** Resolved scale, as stored; 1 = the box as sized. */
  scale: number;
  /** This orientation stores an authored position (never on the basis). */
  positionOwn: boolean;
  /** This orientation stores an authored scale (never on the basis). */
  scaleOwn: boolean;
  /** This orientation stores an authored rotation (never on the basis). */
  rotationOwn: boolean;
  /**
   * Read-only (D-15): the box times this orientation's scale, in stage px and
   * as % of this orientation's stage, each rounded to 0.1. Rotation and the
   * picture's contain fit are not applied.
   */
  onStage: { width: number; height: number; widthPct: number; heightPct: number };
  /** Only where they differ from the sprite's top-level values. */
  rotation?: number;
  flipX?: boolean;
  flipY?: boolean;
}

export interface SpriteView {
  id: string;
  name: string;
  /** Back-to-front rank, 1 = furthest back; set by `placeSprite.layer`. */
  layer: number;
  /** The number of sprites in the scene. */
  layers: number;
  /** The box, canvas px (`resolveSpriteSize`); set by `editSprite.size` / `addSprite.size`. */
  size: SpriteSize;
  /** True when no valid size is stored and the 100×100 fallback plays. */
  sizeDefault: boolean;
  /** 0–1 of the sprite's box; { x: 0.5, y: 0.5 } when none is stored. */
  hinge: { x: number; y: number };
  /** Degrees, the basis orientation's resolved transform. */
  rotation: number;
  flipX: boolean;
  flipY: boolean;
  /** `playback` in full when the state stores one (spec 035): how its spritesheet plays, as `editSprite.setStates` takes it. */
  states: { name: string; mediaId: number | null; mediaName?: string; default: boolean; playback?: PlaybackArg }[];
  /** The orientation holding the base composition (portrait when none is stored). */
  basis: Orientation;
  /** Always all three orientations. */
  orientations: Record<Orientation, SpriteOrientationView>;
  /**
   * Spec 036: every keyframe of the sprite in the operation units (`at`
   * seconds, hinge 0–1), each readable back as the call that writes it.
   */
  keys: KeyView[];
  /** Spec 036: its motion in `describeScene`'s words, uncut; `''` when nothing moves. */
  motion: string;
}

export type GetSpriteErrorCode = 'unknown_sprite' | 'ambiguous_sprite';

export type GetSpriteResult =
  | { ok: true; sprite: SpriteView }
  | { ok: false; error: { code: GetSpriteErrorCode; message: string; sprites: { id: string; name: string }[] } };

const round = (v: number) => Math.round(v * 10) / 10;

function orientationView(s: Sprite, o: Orientation, basis: Orientation, top: Transform, box: SpriteSize): SpriteOrientationView {
  const p = getResponsivePosition(s.layout, o);
  const t = getResponsiveTransform(s.layout, o, s.transform);
  const entry = o === basis ? undefined : (s.layout?.[o] as OrientationLayout | undefined);
  const stored = entry?.position as Partial<Position> | undefined;
  const scale = t.scale ?? 1;
  const stage = STAGE_SIZES[o];
  const w = box.width * scale;
  const h = box.height * scale;
  const view: SpriteOrientationView = {
    x: round(p.x),
    y: round(p.y),
    scale,
    positionOwn: !!stored && (stored.x !== undefined || stored.y !== undefined) && !isDerived(entry, 'position'),
    scaleOwn: entry?.transform?.scale !== undefined && !isDerived(entry, 'scale'),
    rotationOwn: entry?.transform?.rotation !== undefined && !isDerived(entry, 'rotation'),
    onStage: { width: round(w), height: round(h), widthPct: round((w / stage.width) * 100), heightPct: round((h / stage.height) * 100) },
  };
  const rotation = t.rotation ?? 0;
  if (rotation !== (top.rotation ?? 0)) view.rotation = rotation;
  if (!!t.flipX !== !!top.flipX) view.flipX = !!t.flipX;
  if (!!t.flipY !== !!top.flipY) view.flipY = !!t.flipY;
  return view;
}

/** True when the sprite stores a box `resolveSpriteSize` accepts. */
const hasStoredSize = (s: Sprite) => {
  const z = s.size;
  return !!z && z.width > 0 && z.height > 0 && Number.isFinite(z.width) && Number.isFinite(z.height);
};

function view(doc: SceneDocument, s: Sprite, media?: ReadonlyMap<number, OperationMedia>): SpriteView {
  const ordered = backToFront(doc.sprites);
  const basis = getBasisOrientation(s.layout) ?? 'portrait';
  const top = getResponsiveTransform(s.layout, basis, s.transform);
  const size = resolveSpriteSize(s);
  const orientations = {} as Record<Orientation, SpriteOrientationView>;
  for (const o of ORIENTATIONS) orientations[o] = orientationView(s, o, basis, top, size);

  const result: SpriteView = {
    id: s.id,
    name: s.name,
    layer: ordered.indexOf(s) + 1,
    layers: ordered.length,
    size,
    sizeDefault: !hasStoredSize(s),
    hinge: { x: s.hinge?.x ?? 0.5, y: s.hinge?.y ?? 0.5 },
    rotation: top.rotation ?? 0,
    flipX: !!top.flipX,
    flipY: !!top.flipY,
    states: s.states.map((st) => {
      const mediaId = st.mediaId > 0 ? st.mediaId : null;
      const m = mediaId !== null ? media?.get(mediaId) : undefined;
      return {
        name: st.name, mediaId, ...(m ? { mediaName: m.name } : {}), default: st.name === s.defaultState,
        ...(st.playback ? { playback: playbackToArg(st.playback) } : {}),
      };
    }),
    basis,
    orientations,
    keys: keyViews(doc.keyframes?.find((t) => t.trackId === s.id)?.keyframes ?? []),
    motion: motionText(describeMotion(doc, s.id)),
  };
  return result;
}

const idName = (s: Sprite) => ({ id: s.id, name: s.name });

/** How a sprite reference (an id or a name) resolves (D-10, D-11; spec 036 D-10 for the keyframe operations). */
export type SpriteLookup =
  | { ok: true; sprite: Sprite }
  | { ok: false; code: GetSpriteErrorCode; message: string; sprites: { id: string; name: string }[] };

/**
 * The one sprite lookup: an exact id wins, else a name compared trimmed and
 * case-insensitively. No match is `unknown_sprite`, several are
 * `ambiguous_sprite`, each listing sprites back to front. `getSprite` and
 * spec 036's keyframe operations share it.
 */
export function findSprite(doc: SceneDocument, sprite: string): SpriteLookup {
  const byId = doc.sprites.find((s) => s.id === sprite);
  if (byId) return { ok: true, sprite: byId };
  const key = sprite.trim().toLowerCase();
  const matches = backToFront(doc.sprites).filter((s) => (s.name ?? '').trim().toLowerCase() === key);
  if (matches.length === 1) return { ok: true, sprite: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      code: 'ambiguous_sprite',
      message: `${matches.length} sprites are named "${sprite.trim()}" in this scene; pass the sprite's id`,
      sprites: matches.map(idName),
    };
  }
  return { ok: false, code: 'unknown_sprite', message: `no sprite "${sprite}" in this scene`, sprites: backToFront(doc.sprites).map(idName) };
}

/**
 * One sprite's values. `sprite` is an id or a name: an exact id wins, else a
 * name compared trimmed and case-insensitively (D-10). No match fails with
 * `unknown_sprite`, several with `ambiguous_sprite`, each listing sprites
 * back to front (D-11). `media` adds media names to the states.
 */
export function getSprite(doc: SceneDocument, sprite: string, media?: ReadonlyMap<number, OperationMedia>): GetSpriteResult {
  const found = findSprite(doc, sprite);
  if (found.ok === false) return { ok: false, error: { code: found.code, message: found.message, sprites: found.sprites } };
  return { ok: true, sprite: view(doc, found.sprite, media) };
}

// ─── Coverage maps (D-14) ───────────────────────────────────────────────────

/**
 * What becomes of one field of a type `getSprite` reads: projected at these
 * view paths and set by these operation arguments (`op.arg`, a top-level
 * property of that operation's `inputSchema`); projected and never set (the
 * reason); or not projected at all (the reason).
 */
export type FieldCoverage =
  | { view: string[]; set: string[] }
  | { view: string[]; readOnly: string }
  | { excluded: string };

const NO_RENDERER = 'no renderer reads it (evaluateFrame, SceneRuntime and SceneCanvas ignore it)';

export const SPRITE_COVERAGE: Record<keyof Sprite, FieldCoverage> = {
  id: { view: ['id'], readOnly: 'fixed when the sprite is created' },
  name: { view: ['name'], set: ['editSprite.name', 'addSprite.name'] },
  hinge: { view: ['hinge'], set: ['editSprite.hinge'] },
  size: { view: ['size', 'sizeDefault', 'orientations.*.onStage'], set: ['editSprite.size', 'addSprite.size'] },
  layout: { view: ['basis', 'orientations'], set: ['placeSprite.placement', 'editSprite.scale', 'editSprite.rotation', 'editSprite.flipX', 'editSprite.flipY'] },
  transform: { view: ['rotation', 'flipX', 'flipY', 'orientations.*.scale'], set: ['editSprite.scale', 'editSprite.rotation', 'editSprite.flipX', 'editSprite.flipY'] },
  zIndex: { view: ['layer'], set: ['placeSprite.layer', 'placeSprite.inFrontOf', 'placeSprite.behind'] },
  states: { view: ['states'], set: ['addSprite.states', 'editSprite.setStates', 'editSprite.removeStates'] },
  defaultState: { view: ['states.*.default'], set: ['editSprite.defaultState', 'addSprite.defaultState'] },
  currentState: { excluded: 'runtime only, never authored' },
};

export const SPRITE_STATE_COVERAGE: Record<keyof SpriteState, FieldCoverage> = {
  name: { view: ['states.*.name'], set: ['addSprite.states', 'editSprite.setStates'] },
  mediaId: { view: ['states.*.mediaId', 'states.*.mediaName'], set: ['addSprite.states', 'editSprite.setStates'] },
  animation: { excluded: NO_RENDERER },
  transform: { excluded: NO_RENDERER },
  playback: { view: ['states.*.playback'], set: ['addSprite.states', 'editSprite.setStates'] },
};

export const ORIENTATION_LAYOUT_COVERAGE: Record<keyof OrientationLayout, FieldCoverage> = {
  position: { view: ['orientations.*.x', 'orientations.*.y', 'orientations.*.positionOwn'], set: ['placeSprite.placement', 'placeSprite.orientation'] },
  transform: {
    view: ['orientations.*.scale', 'orientations.*.rotation', 'orientations.*.scaleOwn', 'orientations.*.rotationOwn', 'orientations.*.onStage'],
    set: ['editSprite.scale', 'editSprite.rotation', 'editSprite.orientation'],
  },
  visible: { excluded: NO_RENDERER },
  derived: { excluded: 'shown as the own flags (positionOwn, scaleOwn, rotationOwn)' },
};

const ORIENTATION_SET = ['placeSprite.orientation', 'editSprite.orientation'];

export const RESPONSIVE_LAYOUT_COVERAGE: Record<keyof ResponsiveLayout, FieldCoverage> = {
  portrait: { view: ['orientations.portrait'], set: ORIENTATION_SET },
  landscape: { view: ['orientations.landscape'], set: ORIENTATION_SET },
  desktop: { view: ['orientations.desktop'], set: ORIENTATION_SET },
  basis: { view: ['basis'], readOnly: 'set when the sprite is created' },
};

export const TRANSFORM_COVERAGE: Record<keyof Transform, FieldCoverage> = {
  scale: { view: ['orientations.*.scale', 'orientations.*.onStage'], set: ['editSprite.scale'] },
  rotation: { view: ['rotation', 'orientations.*.rotation'], set: ['editSprite.rotation'] },
  // One at-rest flip for every orientation (spec 038 D-2); flips over time are flipSprite's keys.
  flipX: { view: ['flipX', 'orientations.*.flipX'], set: ['editSprite.flipX'] },
  flipY: { view: ['flipY', 'orientations.*.flipY'], set: ['editSprite.flipY'] },
};

export const HINGE_COVERAGE: Record<keyof Hinge, FieldCoverage> = {
  x: { view: ['hinge.x'], set: ['editSprite.hinge'] },
  y: { view: ['hinge.y'], set: ['editSprite.hinge'] },
};

export const SPRITE_SIZE_COVERAGE: Record<keyof SpriteSize, FieldCoverage> = {
  width: { view: ['size.width', 'orientations.*.onStage'], set: ['editSprite.size', 'addSprite.size'] },
  height: { view: ['size.height', 'orientations.*.onStage'], set: ['editSprite.size', 'addSprite.size'] },
};

/**
 * Spec 036 (AC-15): what becomes of each field of a sprite's keyframes in
 * `getSprite`'s `keys`, and which keyframe operation argument writes it.
 */
export const KEYFRAME_COVERAGE: Record<keyof Keyframe, FieldCoverage> = {
  id: { excluded: 'derived per write (kf_<spriteId>_<n>), never an argument' },
  time: { view: ['keys.*.at'], set: ['moveSprite.at', 'setSpriteState.at', 'showSprite.at', 'hideSprite.at', 'flipSprite.at'] },
  property: { view: ['keys.*.property'], set: ['moveSprite.to', 'setSpriteState.state', 'flipSprite.flipX', 'flipSprite.flipY'] },
  value: { view: ['keys.*.value'], set: ['moveSprite.to', 'setSpriteState.state', 'flipSprite.flipX', 'flipSprite.flipY'] },
  easing: { view: ['keys.*.ease'], set: ['moveSprite.ease'] },
  orientation: {
    view: ['keys.*.orientation'],
    set: ['moveSprite.orientation', 'setSpriteState.orientation', 'showSprite.orientation', 'hideSprite.orientation', 'flipSprite.orientation'],
  },
  // Spec 039 D-3: read only on background state keys; never on a sprite's.
  transition: { excluded: 'background state keys only (setBackgroundState.transition); a sprite key carries none' },
};
